import { useRef, useState } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./LoadManagement.module.css";

/* ── Load levels ── */
type LoadLevel = "low" | "medium" | "high" | "overload" | "rest";

const LOAD_CLASS: Record<LoadLevel, string> = {
  low: styles.cellLow,
  medium: styles.cellMedium,
  high: styles.cellHigh,
  overload: styles.cellOverload,
  rest: styles.cellRest,
};

/* LOAD_LABEL and DAYS are resolved from i18n inside the component */

/* ── Per-player detailed data ── */
interface TrainingSession {
  day: string;
  type: string;
  duration: string;
  load: LoadLevel;
  avgHr: number;
  maxHr: number;
}

interface PlayerLoadData {
  number: number;
  name: string;
  pos: string;
  week: LoadLevel[];
  trimp: number;
  rpe: number;
  weeklyTrimp: number[];
  hrvTrend: number[];
  acuteLoad: number;
  chronicLoad: number;
  acwr: number; // acute:chronic workload ratio
  status: "green" | "yellow" | "red";
  statusLabel: string;
  sessions: TrainingSession[];
}

const PLAYERS: PlayerLoadData[] = [
  {
    number: 13,
    name: "B. Adebayo",
    pos: "C",
    week: ["high", "medium", "rest", "high", "low", "overload", "rest"],
    trimp: 82,
    rpe: 8.2,
    weeklyTrimp: [68, 72, 78, 85, 82],
    hrvTrend: [65, 58, 72, 55, 48, 42, 68],
    acuteLoad: 420,
    chronicLoad: 380,
    acwr: 1.11,
    status: "yellow",
    statusLabel: "Elevated",
    sessions: [
      {
        day: "Mon",
        type: "5v5 Scrimmage",
        duration: "45min",
        load: "high",
        avgHr: 158,
        maxHr: 182,
      },
      {
        day: "Tue",
        type: "Shooting Drills",
        duration: "60min",
        load: "medium",
        avgHr: 132,
        maxHr: 155,
      },
      { day: "Thu", type: "Game vs LAL", duration: "38min", load: "high", avgHr: 162, maxHr: 188 },
      { day: "Fri", type: "Light Shoot", duration: "30min", load: "low", avgHr: 112, maxHr: 128 },
      {
        day: "Sat",
        type: "Game vs BKN",
        duration: "42min",
        load: "overload",
        avgHr: 168,
        maxHr: 192,
      },
    ],
  },
  {
    number: 14,
    name: "T. Herro",
    pos: "SG",
    week: ["medium", "high", "low", "medium", "high", "medium", "rest"],
    trimp: 68,
    rpe: 7.1,
    weeklyTrimp: [62, 65, 70, 68, 68],
    hrvTrend: [70, 62, 75, 68, 60, 65, 72],
    acuteLoad: 360,
    chronicLoad: 350,
    acwr: 1.03,
    status: "green",
    statusLabel: "Optimal",
    sessions: [
      {
        day: "Mon",
        type: "Conditioning",
        duration: "40min",
        load: "medium",
        avgHr: 145,
        maxHr: 168,
      },
      { day: "Tue", type: "Game vs BOS", duration: "36min", load: "high", avgHr: 155, maxHr: 180 },
      { day: "Wed", type: "Recovery Run", duration: "20min", load: "low", avgHr: 108, maxHr: 122 },
      {
        day: "Thu",
        type: "Team Practice",
        duration: "50min",
        load: "medium",
        avgHr: 138,
        maxHr: 158,
      },
      { day: "Fri", type: "Game vs CHI", duration: "34min", load: "high", avgHr: 152, maxHr: 178 },
    ],
  },
  {
    number: 2,
    name: "T. Rozier",
    pos: "PG",
    week: ["low", "medium", "low", "medium", "low", "high", "rest"],
    trimp: 45,
    rpe: 5.8,
    weeklyTrimp: [42, 40, 48, 44, 45],
    hrvTrend: [78, 75, 80, 72, 76, 68, 82],
    acuteLoad: 280,
    chronicLoad: 300,
    acwr: 0.93,
    status: "green",
    statusLabel: "Fresh",
    sessions: [
      { day: "Mon", type: "Shooting", duration: "45min", load: "low", avgHr: 115, maxHr: 132 },
      {
        day: "Tue",
        type: "Team Practice",
        duration: "55min",
        load: "medium",
        avgHr: 135,
        maxHr: 152,
      },
      { day: "Wed", type: "Film + Light", duration: "20min", load: "low", avgHr: 98, maxHr: 110 },
      { day: "Thu", type: "Practice", duration: "50min", load: "medium", avgHr: 130, maxHr: 148 },
      { day: "Sat", type: "Game vs BKN", duration: "28min", load: "high", avgHr: 155, maxHr: 175 },
    ],
  },
  {
    number: 22,
    name: "A. Wiggins",
    pos: "SF",
    week: ["rest", "rest", "low", "rest", "low", "rest", "rest"],
    trimp: 18,
    rpe: 3.2,
    weeklyTrimp: [55, 42, 30, 22, 18],
    hrvTrend: [45, 48, 52, 50, 55, 52, 58],
    acuteLoad: 120,
    chronicLoad: 280,
    acwr: 0.43,
    status: "red",
    statusLabel: "Recovery",
    sessions: [
      { day: "Wed", type: "Rehab Walk", duration: "15min", load: "low", avgHr: 95, maxHr: 105 },
      {
        day: "Fri",
        type: "Light Shooting",
        duration: "20min",
        load: "low",
        avgHr: 102,
        maxHr: 115,
      },
    ],
  },
  {
    number: 11,
    name: "J. Jaquez Jr.",
    pos: "SF",
    week: ["medium", "low", "medium", "high", "medium", "low", "rest"],
    trimp: 55,
    rpe: 6.5,
    weeklyTrimp: [48, 52, 58, 55, 55],
    hrvTrend: [72, 70, 68, 62, 66, 70, 74],
    acuteLoad: 320,
    chronicLoad: 310,
    acwr: 1.03,
    status: "green",
    statusLabel: "Optimal",
    sessions: [
      {
        day: "Mon",
        type: "Skills Training",
        duration: "50min",
        load: "medium",
        avgHr: 138,
        maxHr: 155,
      },
      { day: "Tue", type: "Film + Light", duration: "25min", load: "low", avgHr: 105, maxHr: 118 },
      { day: "Wed", type: "Practice", duration: "55min", load: "medium", avgHr: 140, maxHr: 160 },
      { day: "Thu", type: "Game vs LAL", duration: "32min", load: "high", avgHr: 150, maxHr: 175 },
      {
        day: "Fri",
        type: "Shooting Drills",
        duration: "40min",
        load: "medium",
        avgHr: 128,
        maxHr: 145,
      },
    ],
  },
  {
    number: 7,
    name: "K. Ware",
    pos: "C",
    week: ["low", "medium", "high", "medium", "low", "medium", "rest"],
    trimp: 52,
    rpe: 6.0,
    weeklyTrimp: [48, 50, 55, 52, 52],
    hrvTrend: [76, 72, 65, 68, 74, 70, 78],
    acuteLoad: 300,
    chronicLoad: 290,
    acwr: 1.03,
    status: "green",
    statusLabel: "Optimal",
    sessions: [
      {
        day: "Mon",
        type: "Individual Work",
        duration: "35min",
        load: "low",
        avgHr: 112,
        maxHr: 130,
      },
      { day: "Tue", type: "Practice", duration: "50min", load: "medium", avgHr: 135, maxHr: 158 },
      { day: "Wed", type: "Game vs NYK", duration: "22min", load: "high", avgHr: 155, maxHr: 178 },
      { day: "Thu", type: "Recovery", duration: "25min", load: "medium", avgHr: 118, maxHr: 135 },
      { day: "Fri", type: "Shooting", duration: "30min", load: "low", avgHr: 108, maxHr: 125 },
    ],
  },
  {
    number: 24,
    name: "N. Powell",
    pos: "SG",
    week: ["high", "high", "medium", "overload", "rest", "medium", "rest"],
    trimp: 75,
    rpe: 7.8,
    weeklyTrimp: [58, 65, 72, 78, 75],
    hrvTrend: [60, 55, 52, 42, 58, 62, 65],
    acuteLoad: 400,
    chronicLoad: 340,
    acwr: 1.18,
    status: "red",
    statusLabel: "Overreaching",
    sessions: [
      { day: "Mon", type: "Game vs PHI", duration: "38min", load: "high", avgHr: 160, maxHr: 185 },
      { day: "Tue", type: "Practice", duration: "55min", load: "high", avgHr: 152, maxHr: 172 },
      {
        day: "Wed",
        type: "Conditioning",
        duration: "40min",
        load: "medium",
        avgHr: 142,
        maxHr: 162,
      },
      {
        day: "Thu",
        type: "Game vs ATL",
        duration: "40min",
        load: "overload",
        avgHr: 165,
        maxHr: 195,
      },
      {
        day: "Sat",
        type: "Light Practice",
        duration: "35min",
        load: "medium",
        avgHr: 128,
        maxHr: 145,
      },
    ],
  },
  {
    number: 99,
    name: "E. Spoelstra",
    pos: "HC",
    week: ["overload", "overload", "high", "overload", "high", "overload", "high"],
    trimp: 95,
    rpe: 9.5,
    weeklyTrimp: [88, 90, 92, 94, 95],
    hrvTrend: [30, 28, 25, 22, 20, 18, 15],
    acuteLoad: 500,
    chronicLoad: 480,
    acwr: 1.04,
    status: "red",
    statusLabel: "Overreaching",
    sessions: [
      {
        day: "Mon",
        type: "Game vs PHI",
        duration: "48min",
        load: "overload",
        avgHr: 185,
        maxHr: 210,
      },
      {
        day: "Tue",
        type: "Practice",
        duration: "120min",
        load: "overload",
        avgHr: 175,
        maxHr: 205,
      },
      {
        day: "Wed",
        type: "Film + Light",
        duration: "90min",
        load: "high",
        avgHr: 160,
        maxHr: 195,
      },
      {
        day: "Thu",
        type: "Game vs ATL",
        duration: "48min",
        load: "overload",
        avgHr: 190,
        maxHr: 215,
      },
      {
        day: "Sat",
        type: "Team Practice",
        duration: "100min",
        load: "high",
        avgHr: 170,
        maxHr: 200,
      },
    ],
  },
];

/* ── Helpers ── */

function buildSparkline(points: number[], w = 120, h = 40): string {
  const maxVal = Math.max(...points);
  const minVal = Math.min(...points);
  const range = maxVal - minVal || 1;
  const pad = 4;
  return points
    .map((v, i) => {
      const x = pad + (i / (points.length - 1)) * (w - pad * 2);
      const y = pad + (1 - (v - minVal) / range) * (h - pad * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/** TRIMP → color interpolation: green (0) → yellow (50) → red (100) */
function trimpColor(trimp: number): string {
  if (trimp <= 40) return "rgba(52, 199, 89, 0.15)";
  if (trimp <= 60) return "rgba(255, 204, 0, 0.10)";
  if (trimp <= 75) return "rgba(255, 149, 0, 0.12)";
  return "rgba(255, 59, 48, 0.12)";
}

function trimpBorderColor(trimp: number): string {
  if (trimp <= 40) return "rgba(52, 199, 89, 0.35)";
  if (trimp <= 60) return "rgba(255, 204, 0, 0.30)";
  if (trimp <= 75) return "rgba(255, 149, 0, 0.35)";
  return "rgba(255, 59, 48, 0.35)";
}

function statusDotColor(status: string): string {
  if (status === "green") return "#34c759";
  if (status === "yellow") return "#ffcc00";
  return "#ff3b30";
}

function acwrColor(acwr: number): string {
  if (acwr >= 0.8 && acwr <= 1.3) return "#34c759";
  if (acwr < 0.8) return "#0071e3";
  return "#ff3b30";
}

export function LoadManagement() {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const loadLabel: Record<LoadLevel, string> = {
    low: t.aiTeam.loadLevelLow,
    medium: t.aiTeam.loadLevelMed,
    high: t.aiTeam.loadLevelHigh,
    overload: t.aiTeam.loadLevelOver,
    rest: t.aiTeam.loadLevelRest,
  };

  const statusLabelMap: Record<string, string> = {
    Elevated: t.aiTeam.loadStatusElevated,
    Optimal: t.aiTeam.loadStatusOptimal,
    Fresh: t.aiTeam.loadStatusFresh,
    Recovery: t.aiTeam.loadStatusRecovery,
    Overreaching: t.aiTeam.loadStatusOverreaching,
    Injured: t.aiTeam.loadStatusInjured,
  };

  const sessionTypeMap: Record<string, string> = {
    "5v5 Scrimmage": t.aiTeam.loadSessionTypes.scrimmage5v5,
    "Shooting Drills": t.aiTeam.loadSessionTypes.shootingDrills,
    "Game vs LAL": t.aiTeam.loadSessionTypes.gameVsLal,
    "Light Shoot": t.aiTeam.loadSessionTypes.lightShoot,
    "Game vs BKN": t.aiTeam.loadSessionTypes.gameVsBkn,
    Conditioning: t.aiTeam.loadSessionTypes.conditioning,
    "Game vs MIA": t.aiTeam.loadSessionTypes.gameVsMia,
    "Game vs BOS": t.aiTeam.loadSessionTypes.gameVsBos,
    "Game vs NYK": t.aiTeam.loadSessionTypes.gameVsNyk,
    "Recovery Run": t.aiTeam.loadSessionTypes.recoveryRun,
    "Team Practice": t.aiTeam.loadSessionTypes.teamPractice,
    "Game vs CHI": t.aiTeam.loadSessionTypes.gameVsChi,
    Shooting: t.aiTeam.loadSessionTypes.shooting,
    "Film + Light": t.aiTeam.loadSessionTypes.filmLight,
    Practice: t.aiTeam.loadSessionTypes.practice,
    "Rehab Walk": t.aiTeam.loadSessionTypes.rehabWalk,
    "Light Shooting": t.aiTeam.loadSessionTypes.lightShooting,
    "Skills Training": t.aiTeam.loadSessionTypes.skillsTraining,
    "Individual Work": t.aiTeam.loadSessionTypes.individualWork,
    Recovery: t.aiTeam.loadSessionTypes.recovery,
    "Game vs PHI": t.aiTeam.loadSessionTypes.gameVsPhi,
    "Game vs ATL": t.aiTeam.loadSessionTypes.gameVsAtl,
    "Light Practice": t.aiTeam.loadSessionTypes.lightPractice,
    "Pool Rehab": t.aiTeam.loadSessionTypes.poolRehab,
  };

  const dayMap: Record<string, string> = {
    Mon: t.aiTeam.loadDays[0],
    Tue: t.aiTeam.loadDays[1],
    Wed: t.aiTeam.loadDays[2],
    Thu: t.aiTeam.loadDays[3],
    Fri: t.aiTeam.loadDays[4],
    Sat: t.aiTeam.loadDays[5],
    Sun: t.aiTeam.loadDays[6],
  };

  const selected = PLAYERS[selectedIdx];

  const ref = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const layout = el.querySelector("[data-load-layout]");
    if (layout) {
      gsap.set(layout, { opacity: 0, y: 30 });
      gsap.to(layout, {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
      });
    }
  }, []);

  return (
    <section ref={ref} className={styles.section}>
      <h2 className={styles.title}>{t.aiTeam.loadTitle}</h2>
      <p className={styles.desc}>{t.aiTeam.loadDesc}</p>

      <div className={styles.metrics}>
        {t.aiTeam.loadMetrics.map((metric) => (
          <span key={metric} className={styles.metricBadge}>
            {metric}
          </span>
        ))}
      </div>

      <div className={styles.layout} data-load-layout>
        {/* ── Left: Player list ── */}
        <div className={styles.playerList} ref={listRef}>
          <div className={styles.listHeader}>{t.aiTeam.loadPlayersLabel}</div>
          <div className={styles.listScroll}>
            {PLAYERS.map((player, i) => (
              <button
                key={player.number}
                type="button"
                className={`${styles.playerItem} ${i === selectedIdx ? styles.playerItemActive : ""}`}
                style={{
                  background: i === selectedIdx ? trimpColor(player.trimp) : undefined,
                  borderColor: i === selectedIdx ? trimpBorderColor(player.trimp) : undefined,
                }}
                onClick={() => setSelectedIdx(i)}
              >
                <span
                  className={styles.playerDot}
                  style={{ background: statusDotColor(player.status) }}
                />
                <div className={styles.playerInfo}>
                  <span className={styles.playerName}>
                    #{player.number} {player.name}
                  </span>
                  <span className={styles.playerPos}>{player.pos}</span>
                </div>
                <div className={styles.playerMini}>
                  <span
                    className={styles.playerTrimp}
                    style={{ color: statusDotColor(player.status) }}
                  >
                    {player.trimp}
                  </span>
                  <span className={styles.playerTrimpLabel}>TRIMP</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Right: Detail panel ── */}
        <div className={styles.detailPanel}>
          {/* Header */}
          <div className={styles.detailHeader}>
            <div>
              <h3 className={styles.detailName}>
                #{selected.number} {selected.name}
              </h3>
              <span className={styles.detailPos}>{selected.pos}</span>
            </div>
            <div className={styles.statusBadge} style={{ background: trimpColor(selected.trimp) }}>
              <span
                className={styles.statusDot}
                style={{ background: statusDotColor(selected.status) }}
              />
              {statusLabelMap[selected.statusLabel] || selected.statusLabel}
            </div>
          </div>

          {/* Key metrics row */}
          <div className={styles.metricsRow}>
            <div className={styles.metricCard}>
              <span className={styles.metricValue}>{selected.trimp}</span>
              <span className={styles.metricLabel}>TRIMP</span>
              <div className={styles.metricBar}>
                <div
                  className={styles.metricBarFill}
                  style={{
                    width: `${selected.trimp}%`,
                    background: statusDotColor(selected.status),
                  }}
                />
              </div>
            </div>
            <div className={styles.metricCard}>
              <span className={styles.metricValue}>{selected.rpe.toFixed(1)}</span>
              <span className={styles.metricLabel}>RPE</span>
              <div className={styles.metricBar}>
                <div
                  className={styles.metricBarFill}
                  style={{
                    width: `${selected.rpe * 10}%`,
                    background: selected.rpe > 7 ? "#ff9500" : "#34c759",
                  }}
                />
              </div>
            </div>
            <div className={styles.metricCard}>
              <span className={styles.metricValue} style={{ color: acwrColor(selected.acwr) }}>
                {selected.acwr.toFixed(2)}
              </span>
              <span className={styles.metricLabel}>ACWR</span>
              <span className={styles.metricSub}>
                {selected.acuteLoad} / {selected.chronicLoad}
              </span>
            </div>
          </div>

          {/* Weekly heatmap */}
          <div className={styles.weekSection}>
            <div className={styles.sectionLabel}>{t.aiTeam.loadWeeklyLoadLabel}</div>
            <div className={styles.weekGrid}>
              {t.aiTeam.loadDays.map((d, i) => (
                <div key={d} className={styles.weekDay}>
                  <span className={styles.weekDayLabel}>{d}</span>
                  <div className={`${styles.weekCell} ${LOAD_CLASS[selected.week[i]]}`}>
                    {loadLabel[selected.week[i]]}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* HRV + TRIMP trend charts side by side */}
          <div className={styles.chartRow}>
            <div className={styles.chartBox}>
              <div className={styles.sectionLabel}>{t.aiTeam.loadHrvTrendLabel}</div>
              <svg className={styles.chartSvg} viewBox="0 0 120 40" aria-hidden="true">
                <path d={buildSparkline(selected.hrvTrend)} className={styles.chartLine} />
              </svg>
              <div className={styles.chartRange}>
                <span>{Math.min(...selected.hrvTrend)}</span>
                <span>{Math.max(...selected.hrvTrend)} ms</span>
              </div>
            </div>
            <div className={styles.chartBox}>
              <div className={styles.sectionLabel}>{t.aiTeam.loadTrimpTrendLabel}</div>
              <svg className={styles.chartSvg} viewBox="0 0 120 40" aria-hidden="true">
                <path d={buildSparkline(selected.weeklyTrimp)} className={styles.chartLineTrimp} />
              </svg>
              <div className={styles.chartRange}>
                <span>W1</span>
                <span>W5</span>
              </div>
            </div>
          </div>

          {/* Training sessions */}
          <div className={styles.sessionsSection}>
            <div className={styles.sectionLabel}>{t.aiTeam.loadTrainingSessionsLabel}</div>
            <div className={styles.sessionList}>
              {selected.sessions.map((s) => (
                <div key={`${s.day}-${s.type}`} className={styles.sessionRow}>
                  <span className={styles.sessionDay}>{dayMap[s.day] || s.day}</span>
                  <div className={`${styles.sessionLoad} ${LOAD_CLASS[s.load]}`}>
                    {loadLabel[s.load]}
                  </div>
                  <div className={styles.sessionInfo}>
                    <span className={styles.sessionType}>{sessionTypeMap[s.type] || s.type}</span>
                    <span className={styles.sessionDur}>{s.duration}</span>
                  </div>
                  <div className={styles.sessionHr}>
                    <span className={styles.sessionAvg}>{s.avgHr}</span>
                    <span className={styles.sessionMax}>/{s.maxHr}</span>
                    <span className={styles.sessionHrLabel}>BPM</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
